import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react'

interface Props {
  children: ReactNode
  showDetails?: boolean
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  showErrorDetails: boolean
}

export class FunErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { 
      hasError: false,
      showErrorDetails: false
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showErrorDetails: false }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by FunErrorBoundary:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined, showErrorDetails: false })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  toggleErrorDetails = () => {
    this.setState({ showErrorDetails: !this.state.showErrorDetails })
  }

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development'

      return (
        <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4 animate-fade-in">
          <Card className="max-w-2xl w-full p-8 text-center">
            {/* Icon with animation */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-error/20 rounded-full blur-xl animate-pulse" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-error/20 to-error/10 rounded-full flex items-center justify-center border-2 border-error/30">
                  <AlertTriangle className="w-10 h-10 text-error animate-bounce" style={{ animationDuration: '2s' }} />
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
              Ops! Algo não saiu como esperado
            </h1>

            {/* Description */}
            <p className="font-body text-lg text-brand-gray mb-2">
              Encontramos um problema inesperado por aqui.
            </p>
            <p className="font-body text-sm text-brand-gray/70 mb-8">
              Não se preocupe, nossa equipe já foi notificada e está trabalhando nisso!
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                onClick={this.handleReset}
                variant="primary"
                size="lg"
                className="group"
              >
                <RefreshCw className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                Tentar Novamente
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                size="lg"
              >
                <Home className="w-5 h-5 mr-2" />
                Voltar ao Início
              </Button>
            </div>

            {/* Developer Details Toggle */}
            {(isDev || this.props.showDetails) && this.state.error && (
              <div className="mt-8 pt-6 border-t border-brand-gray/20">
                <button
                  onClick={this.toggleErrorDetails}
                  className="text-sm text-brand-gray hover:text-white transition-colors duration-200 flex items-center gap-2 mx-auto font-body group"
                >
                  <Bug className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  {this.state.showErrorDetails ? 'Ocultar' : 'Mostrar'} detalhes técnicos
                </button>

                {this.state.showErrorDetails && (
                  <div className="mt-4 p-4 bg-brand-dark-lighter rounded-monumenta text-left animate-slide-down">
                    <p className="text-xs font-mono text-error mb-2">
                      <strong className="text-white">Erro:</strong> {this.state.error.message}
                    </p>
                    {this.state.error.stack && (
                      <pre className="text-xs font-mono text-brand-gray overflow-x-auto whitespace-pre-wrap">
                        {this.state.error.stack}
                      </pre>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <div className="mt-3 pt-3 border-t border-brand-gray/20">
                        <p className="text-xs font-mono text-white mb-2">
                          <strong>Component Stack:</strong>
                        </p>
                        <pre className="text-xs font-mono text-brand-gray overflow-x-auto whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Fun supportive message */}
            <div className="mt-8 pt-6 border-t border-brand-gray/20">
              <p className="font-body text-sm text-brand-gray/60 italic">
                "A arquitetura monumental resiste até aos tremores inesperados." 🏛️
              </p>
            </div>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
